import React, { useEffect, useMemo, useRef, useState } from "react";
import ChatBubbleRoundedIcon from "@mui/icons-material/ChatBubbleRounded";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SentimentDissatisfiedRoundedIcon from "@mui/icons-material/SentimentDissatisfiedRounded";
import SentimentNeutralRoundedIcon from "@mui/icons-material/SentimentNeutralRounded";
import SentimentSatisfiedAltRoundedIcon from "@mui/icons-material/SentimentSatisfiedAltRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import MuiTooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
  ChartDataLabels,
);

const SENTIMENTS = [
  {
    key: "negative",
    label: "Negative",
    color: "rgb(231, 64, 53)",
  },
  {
    key: "neutral",
    label: "Neutral",
    color: "rgb(248, 190, 0)",
  },
  {
    key: "positive",
    label: "Positive",
    color: "rgb(48, 169, 85)",
  },
];

const HORIZONTAL_BAR_SENTIMENTS = [...SENTIMENTS].reverse();
const SENTIMENT_ICONS = {
  negative: SentimentDissatisfiedRoundedIcon,
  neutral: SentimentNeutralRoundedIcon,
  positive: SentimentSatisfiedAltRoundedIcon,
};
const DASHBOARD_CARD_STYLES = {
  minWidth: 0,
  bgcolor: "#ffffff",
  border: "1px solid #e3e7ee",
  borderRadius: 2,
  boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
};
const MAX_TOPIC_BUBBLES = 30;
const MAX_TOPIC_PHRASE_WORDS = 4;
const MAX_TOPIC_SUMMARY_WORDS = 4;
const TOPIC_SENTIMENT_TOLERANCE = 0.1;
const TOPIC_PHRASE_CONNECTORS = new Set([
  "for",
  "from",
  "in",
  "of",
  "on",
  "to",
  "with",
]);
const TOPIC_LINKING_VERBS = new Set([
  "are",
  "aren't",
  "be",
  "been",
  "feel",
  "feels",
  "felt",
  "is",
  "isn't",
  "seem",
  "seemed",
  "seems",
  "was",
  "wasn't",
  "were",
  "weren't",
]);
const TOPIC_DIRECTIONAL_WORDS = new Set(["fewer", "less", "more"]);
const TOPIC_REQUEST_WORDS = new Set([
  "need",
  "needed",
  "want",
  "wanted",
  "wish",
]);
const PERSON_NAME_TITLES = new Set([
  "dr",
  "miss",
  "mr",
  "mrs",
  "ms",
  "prof",
  "professor",
]);
const SINGLE_WORD_TOPIC_STOP_WORDS = new Set(
  `assignment assignments assessment assessments class classes clear content
  course courses difficult easy engaging exam exams feedback grading hard
  helpful instructor instructors interesting lecture lectures material
  materials module modules organization pace professor professors resource
  resources student students structure teaching useful work working workload`
    .split(/\s+/),
);
const TOPIC_STOP_WORDS = new Set(
  `about above after again against all also always am among an and any anything
  are aren't around as at away back be because been before being below best
  between both but by can can't cannot choice come could couldn't day did didn't
  different do does doesn't doing don't down during each either enough especially
  extremely even ever every fairly feel felt few find first for from further get
  gets getting give
  given go goes going had hadn't has hasn't have haven't having he her here hers
  herself him himself his how however i if in into is isn't it its itself just know
  less like little lot made make many may maybe me might more most much multiple
  must my myself need neither never new no nor not now of off often once one only
  highly on or other our ours ourselves out over own particularly perhaps person
  please quite rather really same say says see seem should shouldn't significantly
  since so some still somewhat such take
  than that the their theirs them themselves then there these they thing things
  think this those through to too under until up us use used using very want was
  wasn't way ways we well were weren't what when where which while who whom why
  will wish with within without won't worst would wouldn't yes you your yours
  yourself yourselves appreciate appreciated appreciating believe believed
  believes enjoy enjoyed enjoying awesome amazing bad better excellent good great
  include included includes including improve improved improves improving love
  loved loving nice okay ok provide provided provides providing recommend
  recommended recommending
  comment comments optional overall preview question questions response responses
  survey`
    .split(/\s+/)
    .filter(Boolean),
);
// Share identical in-flight requests when Strict Mode re-runs an effect.
const commentsSentimentRequests = new Map();

function formatPercentage(value) {
  const roundedValue = Math.round(Number(value) * 10) / 10;

  return `${Number.isInteger(roundedValue) ? roundedValue : roundedValue.toFixed(1)}%`;
}

function formatChartPercentage(value) {
  return `${Number(value).toFixed(1)}%`;
}

function colorWithAlpha(color, alpha) {
  return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
}

function normalizeTopicWord(word) {
  const lowercaseWord = word
    .toLowerCase()
    .replace(/'s$/, "")
    .replace(/^['-]+|['-]+$/g, "");

  if (lowercaseWord.endsWith("ies") && lowercaseWord.length > 5) {
    return `${lowercaseWord.slice(0, -3)}y`;
  }

  if (
    /(?:ches|shes|sses|xes|zes)$/.test(lowercaseWord) &&
    lowercaseWord.length > 5
  ) {
    return lowercaseWord.slice(0, -2);
  }

  if (
    lowercaseWord.endsWith("s") &&
    !/(?:ics|is|ss|us)$/.test(lowercaseWord) &&
    lowercaseWord.length > 4
  ) {
    return lowercaseWord.slice(0, -1);
  }

  return lowercaseWord;
}

function formatTopicPhrase(words) {
  return words
    .map((word, index) =>
      index > 0 && TOPIC_PHRASE_CONNECTORS.has(word)
        ? word
        : `${word.charAt(0).toUpperCase()}${word.slice(1)}`,
    )
    .join(" ");
}

function formatInsightfulTopicLabel(words) {
  if (words[0] === "need") {
    return formatTopicPhrase([...words.slice(1), "needed"]);
  }

  if (words[0] === "want") {
    return formatTopicPhrase([...words.slice(1), "requested"]);
  }

  if (words[0] === "more") {
    return formatTopicPhrase([...words, "needed"]);
  }

  if (words[0] === "less" || words[0] === "fewer") {
    return formatTopicPhrase(["reduce", ...words.slice(1)]);
  }

  if (words[0] === "not" && words[1] === "enough") {
    return formatTopicPhrase(["insufficient", ...words.slice(2)]);
  }

  return formatTopicPhrase(words);
}

function isTopicBoundaryWord(word) {
  return (
    !TOPIC_PHRASE_CONNECTORS.has(word) &&
    (word.length < 3 || word.includes("'") || TOPIC_STOP_WORDS.has(word))
  );
}

function getFollowingTopicWords(
  words,
  startIndex,
  maximumContentWords = 3,
  excludedWords = new Set(),
) {
  const topicWords = [];
  let contentWordCount = 0;
  let hasStarted = false;

  for (
    let wordIndex = startIndex;
    wordIndex < words.length && wordIndex < startIndex + 7;
    wordIndex += 1
  ) {
    const word = words[wordIndex];

    if (excludedWords.has(word)) {
      if (hasStarted) {
        break;
      }

      continue;
    }

    if (TOPIC_PHRASE_CONNECTORS.has(word)) {
      if (hasStarted) {
        topicWords.push(word);
      }

      continue;
    }

    if (isTopicBoundaryWord(word)) {
      if (hasStarted) {
        break;
      }

      continue;
    }

    topicWords.push(word);
    contentWordCount += 1;
    hasStarted = true;

    if (contentWordCount >= maximumContentWords) {
      break;
    }
  }

  while (
    topicWords.length > 0 &&
    TOPIC_PHRASE_CONNECTORS.has(topicWords[topicWords.length - 1])
  ) {
    topicWords.pop();
  }

  return topicWords;
}

function getDynamicTopicCandidates(comment) {
  const candidates = new Map();
  const clauses = comment
    .replace(/\b(Dr|Prof|Mr|Mrs|Ms)\./gi, "$1 ")
    .split(/[.!?;,:()[\]{}]+/);
  const addCandidate = (candidate) => {
    const existingCandidate = candidates.get(candidate.key);

    if (!existingCandidate) {
      candidates.set(candidate.key, candidate);
      return;
    }

    candidates.set(candidate.key, {
      ...existingCandidate,
      boundaryScore: Math.max(
        existingCandidate.boundaryScore,
        candidate.boundaryScore,
      ),
      insightWeight: Math.max(
        existingCandidate.insightWeight ?? 0,
        candidate.insightWeight ?? 0,
      ),
    });
  };

  clauses.forEach((clause) => {
    const rawWords = clause.match(/[a-z][a-z'-]*/gi) ?? [];
    const surfaceWords = rawWords.map((word) =>
      word.toLowerCase().replace(/'s$/, "").replace(/^['-]+|['-]+$/g, ""),
    );
    const words = surfaceWords.map(normalizeTopicWord).filter(Boolean);
    const likelyNameWords = new Set();

    rawWords.forEach((rawWord, wordIndex) => {
      const word = words[wordIndex];
      const previousWord = surfaceWords[wordIndex - 1];
      const previousRawWord = rawWords[wordIndex - 1] ?? "";
      const nextRawWord = rawWords[wordIndex + 1] ?? "";
      const looksCapitalized = /^[A-Z][a-z'-]+$/.test(rawWord);
      const followsNameTitle = PERSON_NAME_TITLES.has(previousWord);
      const isPartOfCapitalizedName =
        looksCapitalized &&
        (/^[A-Z][a-z'-]+$/.test(previousRawWord) ||
          /^[A-Z][a-z'-]+$/.test(nextRawWord));
      const isPossessiveName = looksCapitalized && /'s$/i.test(rawWord);
      const isCapitalizedWithinSentence = looksCapitalized && wordIndex > 0;

      if (
        PERSON_NAME_TITLES.has(surfaceWords[wordIndex]) ||
        followsNameTitle ||
        isPartOfCapitalizedName ||
        isPossessiveName ||
        isCapitalizedWithinSentence
      ) {
        likelyNameWords.add(word);
      }
    });

    for (
      let phraseLength = 1;
      phraseLength <= MAX_TOPIC_PHRASE_WORDS;
      phraseLength += 1
    ) {
      for (
        let startIndex = 0;
        startIndex <= words.length - phraseLength;
        startIndex += 1
      ) {
        const phraseWords = words.slice(startIndex, startIndex + phraseLength);
        const phraseSurfaceWords = surfaceWords.slice(
          startIndex,
          startIndex + phraseLength,
        );
        const firstWord = phraseWords[0];
        const lastWord = phraseWords[phraseWords.length - 1];
        const contentWords = phraseWords.filter(
          (word) => !TOPIC_PHRASE_CONNECTORS.has(word),
        );
        const hasUnhelpfulWord = phraseWords.some(isTopicBoundaryWord);
        const containsLikelyName = phraseWords.some((word) =>
          likelyNameWords.has(word),
        );

        if (
          TOPIC_PHRASE_CONNECTORS.has(firstWord) ||
          TOPIC_PHRASE_CONNECTORS.has(lastWord) ||
          hasUnhelpfulWord ||
          containsLikelyName ||
          (phraseLength === 1 &&
            SINGLE_WORD_TOPIC_STOP_WORDS.has(firstWord)) ||
          (phraseLength > 1 && contentWords.length < 2)
        ) {
          continue;
        }

        const key = phraseWords.join(" ");
        const endIndex = startIndex + phraseLength;
        const boundaryScore =
          Number(startIndex === 0 || isTopicBoundaryWord(words[startIndex - 1])) +
          Number(endIndex === words.length || isTopicBoundaryWord(words[endIndex]));

        addCandidate({
          key,
          label: formatTopicPhrase(phraseSurfaceWords),
          words: phraseWords,
          contentWordCount: contentWords.length,
          boundaryScore,
          insightWeight: 0,
        });

        const hasNearbyNegation =
          phraseLength === 1 &&
          words
            .slice(Math.max(0, startIndex - 2), startIndex)
            .some((word) => word === "no" || word === "not" || word === "never");

        if (hasNearbyNegation) {
          const negatedWords = ["not", ...phraseWords];

          addCandidate({
            key: negatedWords.join(" "),
            label: formatTopicPhrase(negatedWords),
            words: negatedWords,
            contentWordCount: 1,
            boundaryScore: 2,
            insightWeight: 1,
          });
        }
      }
    }

    const addDirectedTopic = (prefixWords, targetStartIndex) => {
      const targetWords = getFollowingTopicWords(
        words,
        targetStartIndex,
        3,
        likelyNameWords,
      );

      if (targetWords.length === 0) {
        return;
      }

      const phraseWords = [...prefixWords, ...targetWords];

      addCandidate({
        key: phraseWords.join(" "),
        label: formatInsightfulTopicLabel(phraseWords),
        words: phraseWords,
        contentWordCount: phraseWords.filter(
          (word) => !TOPIC_PHRASE_CONNECTORS.has(word),
        ).length,
        boundaryScore: 2,
        insightWeight: 2,
      });
    };

    words.forEach((word, wordIndex) => {
      if (TOPIC_DIRECTIONAL_WORDS.has(word)) {
        addDirectedTopic([word], wordIndex + 1);
      }

      if (word === "too") {
        const quantityWord = words[wordIndex + 1];
        const hasQuantityWord = quantityWord === "many" || quantityWord === "much";

        addDirectedTopic(
          [word, ...(hasQuantityWord ? [quantityWord] : [])],
          wordIndex + (hasQuantityWord ? 2 : 1),
        );
      }

      if (word === "not" && words[wordIndex + 1] === "enough") {
        addDirectedTopic(["not", "enough"], wordIndex + 2);
      }

      if (TOPIC_REQUEST_WORDS.has(word)) {
        const requestWord = word.startsWith("need") ? "need" : "want";
        const qualifierWord = words[wordIndex + 1];
        const hasQualifier = TOPIC_DIRECTIONAL_WORDS.has(qualifierWord);

        addDirectedTopic(
          hasQualifier ? [qualifierWord] : [requestWord],
          wordIndex + (hasQualifier ? 2 : 1),
        );
      }
    });

    words.forEach((word, linkingVerbIndex) => {
      if (!TOPIC_LINKING_VERBS.has(word)) {
        return;
      }

      const subjectWords = [];
      let subjectContentWordCount = 0;

      for (
        let wordIndex = linkingVerbIndex - 1;
        wordIndex >= 0 && subjectContentWordCount < 2;
        wordIndex -= 1
      ) {
        const subjectWord = words[wordIndex];

        if (
          isTopicBoundaryWord(subjectWord) ||
          likelyNameWords.has(subjectWord)
        ) {
          break;
        }

        subjectWords.unshift(subjectWord);

        if (!TOPIC_PHRASE_CONNECTORS.has(subjectWord)) {
          subjectContentWordCount += 1;
        }
      }

      while (
        subjectWords.length > 0 &&
        TOPIC_PHRASE_CONNECTORS.has(subjectWords[0])
      ) {
        subjectWords.shift();
      }

      let descriptorWord = "";
      let descriptorQualifier = "";
      let isNegated = word.endsWith("n't");

      for (
        let wordIndex = linkingVerbIndex + 1;
        wordIndex < words.length && wordIndex <= linkingVerbIndex + 3;
        wordIndex += 1
      ) {
        const candidateWord = words[wordIndex];

        if (candidateWord === "too") {
          descriptorQualifier = "too";
          continue;
        }

        if (candidateWord === "not" || candidateWord === "never") {
          isNegated = true;
          continue;
        }

        if (
          !isTopicBoundaryWord(candidateWord) &&
          !likelyNameWords.has(candidateWord) &&
          !TOPIC_PHRASE_CONNECTORS.has(candidateWord)
        ) {
          descriptorWord = candidateWord;
          break;
        }
      }

      if (
        subjectContentWordCount === 0 ||
        !descriptorWord ||
        subjectWords.includes(descriptorWord)
      ) {
        return;
      }

      const phraseWords = [
        ...(isNegated ? ["not"] : []),
        ...(descriptorQualifier ? [descriptorQualifier] : []),
        descriptorWord,
        ...subjectWords,
      ];

      addCandidate({
        key: phraseWords.join(" "),
        label: formatTopicPhrase(phraseWords),
        words: phraseWords,
        contentWordCount: subjectContentWordCount + 1,
        boundaryScore: 2,
        insightWeight: 2,
      });
    });
  });

  return Array.from(candidates.values());
}

function topicsOverlap(leftTopic, rightTopic) {
  const leftWords = leftTopic.words.filter(
    (word) => !TOPIC_PHRASE_CONNECTORS.has(word),
  );
  const rightWords = rightTopic.words.filter(
    (word) => !TOPIC_PHRASE_CONNECTORS.has(word),
  );
  const hasNestedWords =
    leftWords.every((word) => rightWords.includes(word)) ||
    rightWords.every((word) => leftWords.includes(word));

  if (!hasNestedWords) {
    return false;
  }

  const rightCommentIds = new Set(
    rightTopic.matchingComments.map(({ id }) => id),
  );
  const sharedCommentCount = leftTopic.matchingComments.filter(({ id }) =>
    rightCommentIds.has(id),
  ).length;
  const smallerCommentCount = Math.min(
    leftTopic.matchingComments.length,
    rightTopic.matchingComments.length,
  );

  return sharedCommentCount / smallerCommentCount >= 0.8;
}

function getDynamicTopicSummaryLabel(topic) {
  const topicContentWords = topic.words.filter(
    (word) => !TOPIC_PHRASE_CONNECTORS.has(word),
  );
  const summaries = new Map();

  topic.matchingComments.forEach(({ comment }) => {
    getDynamicTopicCandidates(comment).forEach((candidate) => {
      const candidateLabelWordCount = candidate.label.trim().split(/\s+/).length;
      const candidateContentWords = candidate.words.filter(
        (word) => !TOPIC_PHRASE_CONNECTORS.has(word),
      );
      const sharedWordCount = candidateContentWords.filter((word) =>
        topicContentWords.includes(word),
      ).length;
      const requiredSharedWordCount = Math.max(
        1,
        Math.ceil(
          Math.min(topicContentWords.length, candidateContentWords.length) / 2,
        ),
      );

      if (
        candidate.contentWordCount < 2 ||
        candidateLabelWordCount > MAX_TOPIC_SUMMARY_WORDS ||
        sharedWordCount < requiredSharedWordCount
      ) {
        return;
      }

      if (!summaries.has(candidate.key)) {
        summaries.set(candidate.key, {
          ...candidate,
          matchingCommentCount: 0,
          totalBoundaryScore: 0,
          sharedWordCount,
        });
      }

      const summary = summaries.get(candidate.key);
      summary.matchingCommentCount += 1;
      summary.totalBoundaryScore += candidate.boundaryScore;
      summary.insightWeight = Math.max(
        summary.insightWeight ?? 0,
        candidate.insightWeight ?? 0,
      );
    });
  });

  const minimumSummaryMentions =
    topic.matchingComments.length <= 2
      ? 1
      : Math.max(2, Math.ceil(topic.matchingComments.length * 0.2));
  const rankedSummaries = Array.from(summaries.values())
    .filter(
      ({ matchingCommentCount }) =>
        matchingCommentCount >= minimumSummaryMentions,
    )
    .map((summary) => {
      const boundaryCompleteness =
        summary.totalBoundaryScore / (summary.matchingCommentCount * 2);
      const topicCoverage =
        summary.sharedWordCount / Math.max(topicContentWords.length, 1);
      const conciseLengthBonus =
        summary.contentWordCount === 3
          ? 0.4
          : summary.contentWordCount === 2
            ? 0.3
            : 0.2;

      return {
        ...summary,
        summaryScore:
          summary.matchingCommentCount +
          (summary.insightWeight ?? 0) * 3 +
          boundaryCompleteness +
          topicCoverage * 0.75 +
          conciseLengthBonus,
      };
    })
    .sort(
      (left, right) =>
        right.summaryScore - left.summaryScore ||
        right.matchingCommentCount - left.matchingCommentCount ||
        right.contentWordCount - left.contentWordCount ||
        left.label.localeCompare(right.label),
    );

  return rankedSummaries[0]?.label ?? topic.label;
}

function createTopicCommentSelection(topic) {
  const commentsByQuestion = topic.matchingComments.reduce(
    (groups, { questionText, comment }) => {
      const groupLabel = questionText || "Other comments";

      if (!groups.has(groupLabel)) {
        groups.set(groupLabel, []);
      }

      groups.get(groupLabel).push(comment);
      return groups;
    },
    new Map(),
  );

  return {
    descriptorName: "Topic",
    descriptorLabel: topic.label,
    groups: Array.from(
      commentsByQuestion,
      ([questionText, comments]) => ({ questionText, comments }),
    ),
  };
}

function getBubbleLabelLines(label) {
  const words = label.trim().split(/\s+/);

  if (words.length <= 1) {
    return words;
  }

  const lineCount = words.length >= 5 ? 3 : 2;
  let bestLines = [label];
  let bestLengthDifference = Number.POSITIVE_INFINITY;

  const evaluateLines = (lines) => {
    const lineLengths = lines.map((line) => line.length);
    const lengthDifference =
      Math.max(...lineLengths) - Math.min(...lineLengths);

    if (lengthDifference < bestLengthDifference) {
      bestLines = lines;
      bestLengthDifference = lengthDifference;
    }
  };

  for (let firstSplit = 1; firstSplit < words.length; firstSplit += 1) {
    if (lineCount === 2) {
      evaluateLines([
        words.slice(0, firstSplit).join(" "),
        words.slice(firstSplit).join(" "),
      ]);
      continue;
    }

    for (
      let secondSplit = firstSplit + 1;
      secondSplit < words.length;
      secondSplit += 1
    ) {
      evaluateLines([
        words.slice(0, firstSplit).join(" "),
        words.slice(firstSplit, secondSplit).join(" "),
        words.slice(secondSplit).join(" "),
      ]);
    }
  }

  return bestLines;
}

function packTopicBubbles(topics) {
  if (topics.length === 0) {
    return { bubbles: [], viewBox: "-1 -1 2 2" };
  }

  const collisionPadding = 2;
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const horizontalSpread = 3.4;
  const placedBubbles = [];
  const sortedTopics = [...topics].sort(
    (left, right) =>
      right.size - left.size ||
      right.analyzedCommentCount - left.analyzedCommentCount ||
      left.label.localeCompare(right.label),
  );

  sortedTopics.forEach((topic, topicIndex) => {
    const radius = topic.size / 2;

    if (topicIndex === 0) {
      placedBubbles.push({ ...topic, radius, x: 0, y: 0 });
      return;
    }

    let position;

    for (let distance = 0; distance <= 1600 && !position; distance += 2) {
      const circumference = 2 * Math.PI * Math.max(distance, 1);
      const sampleCount = Math.max(
        12,
        Math.ceil(circumference / Math.max(radius * 0.55, 4)),
      );

      for (let sample = 0; sample < sampleCount; sample += 1) {
        const angle =
          (sample / sampleCount) * Math.PI * 2 + topicIndex * goldenAngle;
        const candidate = {
          x: Math.cos(angle) * distance * horizontalSpread,
          y: Math.sin(angle) * distance,
        };
        const collides = placedBubbles.some(
          (placedBubble) =>
            Math.hypot(
              candidate.x - placedBubble.x,
              candidate.y - placedBubble.y,
            ) <
            radius + placedBubble.radius + collisionPadding,
        );

        if (!collides) {
          position = candidate;
          break;
        }
      }
    }

    placedBubbles.push({
      ...topic,
      radius,
      x: position?.x ?? topicIndex * (radius * 2 + collisionPadding),
      y: position?.y ?? 0,
    });
  });

  const minimumX = Math.min(
    ...placedBubbles.map(({ x, radius }) => x - radius),
  );
  const maximumX = Math.max(
    ...placedBubbles.map(({ x, radius }) => x + radius),
  );
  const minimumY = Math.min(
    ...placedBubbles.map(({ y, radius }) => y - radius),
  );
  const maximumY = Math.max(
    ...placedBubbles.map(({ y, radius }) => y + radius),
  );
  const boundsPadding = 5;
  const contentWidth = maximumX - minimumX + boundsPadding * 2;
  const contentHeight = maximumY - minimumY + boundsPadding * 2;

  return {
    bubbles: placedBubbles,
    viewBox: `${minimumX - boundsPadding} ${
      minimumY - boundsPadding
    } ${contentWidth} ${contentHeight}`,
  };
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  rotation: -90,
  circumference: 180,
  cutout: "60%",

  animation: {
    animateRotate: true,
    animateScale: false,
    duration: 1000,
    easing: "easeOutQuart",

    delay: (context) => {
      // Don't delay labels, tooltips, or hover animations.
      if (context.type !== "data" || context.active) {
        return 0;
      }

      // Negative: 500ms
      // Neutral: 1100ms
      // Positive: 1700ms
      return 500 + context.dataIndex * 600;
    },
  },

  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    datalabels: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const count = context.parsed;
          // return `${context.label}: ${count} ${count === 1 ? "comment" : "comments"}`;
          return ` ${count} ${count === 1 ? "comment" : "comments"}`;
        },
      },
    },
  },
};

const horizontalBarOptions = {
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: {
      left: 8,
      right: 64,
    },
  },
  animation: {
    duration: 1000,
    easing: "easeOutQuart",

    delay: (context) => {
      // Don't delay labels, tooltips, or hover animations.
      if (context.type !== "data" || context.active) {
        return 0;
      }

      // Positive: 500ms
      // Neutral: 1100ms
      // Negative: 1700ms
      return 500 + context.dataIndex * 600;
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      min: 0,
      max: 100,
      title: {
        display: true,
        text: "Percentage of analyzed comments",
        color: "#344054",
        font: {
          size: 12,
          weight: "normal",
        },
      },
      ticks: {
        stepSize: 25,
        color: "#344054",
        font: {
          size: 12,
        },
        callback: (value) => formatPercentage(value),
      },
      border: {
        color: "#d9dee7",
      },
      grid: {
        color: "#e7eaf0",
      },
    },
    y: {
      ticks: {
        color: "#25304a",
        font: {
          size: 12,
        },
      },
      border: {
        display: false,
      },
      grid: {
        display: false,
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    datalabels: {
      anchor: "end",
      align: "right",
      offset: 3,
      clamp: true,
      color: "#1f2937",
      display: true,
      font: {
        size: 12,
        weight: "normal",
      },
      formatter: (value) => formatChartPercentage(value),
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const count = context.dataset.commentCounts[context.dataIndex];

          return ` ${count} ${count === 1 ? "comment" : "comments"}`;
        },
      },
    },
  },
};

function createClickableChartOptions(baseOptions, onDataClick) {
  return {
    ...baseOptions,
    onClick: (_event, elements) => {
      const dataIndex = elements[0]?.index;

      if (typeof dataIndex === "number") {
        onDataClick(dataIndex);
      }
    },
    onHover: (event, elements) => {
      const canvas = event.native?.target;

      if (canvas) {
        canvas.style.cursor = elements.length > 0 ? "pointer" : "default";
      }
    },
  };
}

function SentimentCommentsDialog({ selection, onClose }) {
  const groups = selection?.groups ?? [];
  const descriptorName = selection?.descriptorName ?? "Sentiment";
  const descriptorLabel =
    selection?.descriptorLabel ?? selection?.sentimentLabel ?? "Comments";

  return (
    <Dialog
      open={Boolean(selection)}
      onClose={onClose}
      disableRestoreFocus
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          m: { xs: 2, sm: 3 },
          width: {
            xs: "calc(100% - 32px)",
            sm: "calc(100% - 48px)",
          },
          maxWidth: 1480,
        },
      }}
      aria-label={selection ? `${descriptorLabel} comments` : "Comments"}
    >
      <IconButton
        aria-label="Close comments"
        onClick={onClose}
        sx={(theme) => ({
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 1,
          p: 0.5,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ p: 5, "&:first-of-type": { pt: 5 } }}>
        {groups.map(({ questionText, comments }, groupIndex) => (
          <TableContainer
            key={`${groupIndex}-${questionText}`}
            sx={{ mb: groupIndex < groups.length - 1 ? 3 : 0 }}
          >
            <Table size="small">
              <TableHead
                sx={{
                  borderRight: "2px solid #ccc",
                  borderLeft: "2px solid #ccc",
                  borderTop: "2px solid #ccc",
                }}
              >
                <TableRow>
                  <TableCell
                    align="left"
                    sx={{ borderRight: "1px solid #ccc", width: 200 }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      Question:
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ borderRight: "1px solid #ccc" }}
                  >
                    <Typography variant="body2">{questionText}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="left"
                    sx={{ borderRight: "1px solid #ccc", width: 200 }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      {descriptorName}:
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ borderRight: "1px solid #ccc" }}
                  >
                    <Typography variant="body2">
                      {descriptorLabel}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="left"
                    sx={{ borderRight: "1px solid #ccc", width: 200 }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      Comments:
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ borderRight: "1px solid #ccc" }}
                  >
                    <Typography variant="body2">
                      {comments.length}{" "}
                      {comments.length === 1 ? "comment" : "comments"}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  borderRight: "2px solid #ccc",
                  borderLeft: "2px solid #ccc",
                  borderBottom: "2px solid #ccc",
                }}
              >
                {comments.map((comment, index) => (
                  <TableRow key={`${index}-${comment}`}>
                    <TableCell
                      align="left"
                      colSpan={2}
                      sx={{ borderRight: "1px solid #ccc" }}
                    >
                      <Typography variant="body2" component="div">
                        {comment}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ))}
      </DialogContent>
    </Dialog>
  );
}

async function requestCommentsSentiment(data, token) {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_HOST}/spot/get_comments_sentiment/`,
    {
      method: "POST",
      headers: {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );

  if (!response.ok) {
    const message = await response.text();

    throw new Error(
      `Request failed (${response.status}): ${message || response.statusText}`,
    );
  }

  const result = await response.json();

  if (!Array.isArray(result)) {
    throw new Error("The server returned an unexpected response.");
  }

  return result;
}

function getCommentsSentiment(data) {
  const token = sessionStorage.getItem("token");
  const requestKey = JSON.stringify([token, data]);

  if (!commentsSentimentRequests.has(requestKey)) {
    const request = requestCommentsSentiment(data, token).finally(() => {
      commentsSentimentRequests.delete(requestKey);
    });

    commentsSentimentRequests.set(requestKey, request);
  }

  return commentsSentimentRequests.get(requestKey);
}

function CommentsAnalysis({ data }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [commentSentiments, setCommentSentiments] = useState([]);
  const [selectedCommentGroup, setSelectedCommentGroup] = useState(null);
  const doughnutChartRef = useRef(null);
  const horizontalChartRefs = useRef(new Map());
  const selectedTopicBubbleRef = useRef(null);

  const closeCommentsDialog = () => {
    selectedTopicBubbleRef.current?.blur?.();
    selectedTopicBubbleRef.current = null;
    setSelectedCommentGroup(null);
  };

  useEffect(() => {
    let cancelled = false;
    setSelectedCommentGroup(null);

    async function loadSentiments() {
      if (!data?.results) {
        setCommentSentiments([]);
        setError("");
        setLoading(false);
        return;
      }

      const commentsData = Object.values(data.results).flatMap((question) =>
        Object.values(question?.responses ?? {})
          .filter(
            (comment) =>
              typeof comment === "string" && comment.trim().length > 0,
          )
          .map((comment) => [question?.q_text ?? "", comment.trim()]),
      );

      if (commentsData.length === 0) {
        setCommentSentiments([]);
        setError("");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const result = await getCommentsSentiment(commentsData);

        if (!cancelled) {
          setCommentSentiments(result);
        }
      } catch (requestError) {
        if (!cancelled) {
          console.error(requestError);
          setError(
            requestError.message ||
              "Something went wrong while analyzing comments.",
          );
          setCommentSentiments([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadSentiments();

    return () => {
      cancelled = true;
    };
  }, [data]);

  const sentimentSummary = useMemo(() => {
    const counts = commentSentiments.reduce(
      (result, item) => {
        const sentiment = item?.[2]?.toLowerCase();

        if (Object.hasOwn(result, sentiment)) {
          result[sentiment] += 1;
        }

        return result;
      },
      {
        negative: 0,
        neutral: 0,
        positive: 0,
      },
    );
    const total = SENTIMENTS.reduce(
      (sum, { key }) => sum + counts[key],
      0,
    );
    const percentages = SENTIMENTS.reduce((result, { key }) => {
      result[key] = total === 0 ? 0 : (counts[key] / total) * 100;
      return result;
    }, {});
    const dominantSentiment = SENTIMENTS.reduce((current, sentiment) =>
      counts[sentiment.key] > counts[current.key] ? sentiment : current,
    );

    return {
      counts,
      dominantSentiment,
      percentages,
      total,
    };
  }, [commentSentiments]);

  const chartData = useMemo(
    () => ({
      labels: SENTIMENTS.map(({ label }) => label),
      datasets: [
        {
          data: SENTIMENTS.map(({ key }) => sentimentSummary.counts[key]),
          backgroundColor: SENTIMENTS.map(({ color }) => color),
          borderColor: "#ffffff",
          borderWidth: 2,
          hoverOffset: 6,
        },
      ],
    }),
    [sentimentSummary.counts],
  );

  const summaryMetrics = useMemo(
    () => [
      {
        key: "total",
        label: "Total Comments",
        value: sentimentSummary.total,
        color: "#1565c0",
        backgroundColor: "rgba(21, 101, 192, 0.1)",
        icon: ChatBubbleRoundedIcon,
      },
      ...SENTIMENTS.map((sentiment) => ({
        ...sentiment,
        value: formatPercentage(sentimentSummary.percentages[sentiment.key]),
        icon: SENTIMENT_ICONS[sentiment.key],
      })),
    ],
    [sentimentSummary],
  );

  const doughnutOptions = useMemo(
    () =>
      createClickableChartOptions(chartOptions, (dataIndex) => {
        const sentiment = SENTIMENTS[dataIndex];

        if (!sentiment) {
          return;
        }

        const commentsByQuestion = commentSentiments.reduce((result, item) => {
          const questionText =
            typeof item?.[0] === "string" ? item[0].trim() : "";
          const comment = typeof item?.[1] === "string" ? item[1] : "";
          const itemSentiment = item?.[2]?.toLowerCase();

          if (!questionText || !comment || itemSentiment !== sentiment.key) {
            return result;
          }

          if (!result.has(questionText)) {
            result.set(questionText, []);
          }

          result.get(questionText).push(comment);
          return result;
        }, new Map());
        const groups = Array.from(
          commentsByQuestion,
          ([questionText, comments]) => ({ questionText, comments }),
        );

        if (groups.length === 0) {
          return;
        }

        setSelectedCommentGroup({
          sentimentLabel: sentiment.label,
          groups,
        });
      }),
    [commentSentiments],
  );

  const questionCharts = useMemo(() => {
    const results = data?.results;

    if (!results || typeof results !== "object") {
      return [];
    }

    const commentsByQuestion = commentSentiments.reduce((result, item) => {
      const questionText = typeof item?.[0] === "string" ? item[0].trim() : "";
      const comment = typeof item?.[1] === "string" ? item[1] : "";
      const sentiment = item?.[2]?.toLowerCase();

      if (
        !questionText ||
        !comment ||
        !SENTIMENTS.some(({ key }) => key === sentiment)
      ) {
        return result;
      }

      if (!result.has(questionText)) {
        result.set(questionText, {
          negative: [],
          neutral: [],
          positive: [],
        });
      }

      result.get(questionText)[sentiment].push(comment);
      return result;
    }, new Map());

    return Object.entries(results).map(([questionId, question]) => {
      const questionText = question?.q_text?.trim() ?? "";
      const responses = Array.isArray(question?.responses)
        ? question.responses
        : [];
      const responseCount =
        responses.length === 0 || responses[0] === null ? 0 : responses.length;
      const totalResponses =
        typeof data?.qtally === "number" ? data.qtally : 0;
      const responseRate =
        totalResponses === 0 ? 0 : (responseCount / totalResponses) * 100;
      const sentimentComments = commentsByQuestion.get(questionText) ?? {
        negative: [],
        neutral: [],
        positive: [],
      };
      const total = SENTIMENTS.reduce(
        (sum, { key }) => sum + sentimentComments[key].length,
        0,
      );
      const sentimentCounts = SENTIMENTS.reduce((result, { key }) => {
        result[key] = sentimentComments[key].length;
        return result;
      }, {});
      const dominantSentiment = SENTIMENTS.reduce((current, sentiment) =>
        sentimentCounts[sentiment.key] > sentimentCounts[current.key]
          ? sentiment
          : current,
      );

      return {
        questionId,
        questionText,
        responseCount,
        totalResponses,
        responseRate,
        analyzedCommentCount: total,
        dominantSentiment,
        data: {
          labels: HORIZONTAL_BAR_SENTIMENTS.map(({ label }) => label),
          datasets: [
            {
              data: HORIZONTAL_BAR_SENTIMENTS.map(({ key }) =>
                total === 0
                  ? 0
                  : (sentimentComments[key].length / total) * 100,
              ),
              commentCounts: HORIZONTAL_BAR_SENTIMENTS.map(
                ({ key }) => sentimentComments[key].length,
              ),
              backgroundColor: HORIZONTAL_BAR_SENTIMENTS.map(
                ({ color }) => color,
              ),
              borderColor: "#ffffff",
              borderWidth: 1,
              borderRadius: 4,
            },
          ],
        },
        options: createClickableChartOptions(
          horizontalBarOptions,
          (dataIndex) => {
            const sentiment = HORIZONTAL_BAR_SENTIMENTS[dataIndex];
            const comments = sentiment ? sentimentComments[sentiment.key] : [];

            if (!sentiment || comments.length === 0) {
              return;
            }

            setSelectedCommentGroup({
              sentimentLabel: sentiment.label,
              groups: [{ questionText, comments }],
            });
          },
        ),
      };
    });
  }, [commentSentiments, data]);

  const topicSummaries = useMemo(() => {
    const commentRecords = commentSentiments.reduce((records, item) => {
      const questionText = typeof item?.[0] === "string" ? item[0].trim() : "";
      const comment = typeof item?.[1] === "string" ? item[1].trim() : "";
      const sentiment = item?.[2]?.toLowerCase();

      if (
        comment &&
        SENTIMENTS.some(({ key }) => key === sentiment)
      ) {
        records.push({
          id: records.length,
          questionText,
          comment,
          sentiment,
        });
      }

      return records;
    }, []);
    const commentQuestionCount = Math.max(
      1,
      new Set(
        commentRecords.map(
          ({ questionText }) => questionText || "__unlabeled_question__",
        ),
      ).size,
    );
    const minimumTopicBubbleCount = Math.min(
      MAX_TOPIC_BUBBLES,
      Math.ceil(commentRecords.length / commentQuestionCount),
    );
    const commentsByTopic = new Map();

    commentRecords.forEach((record) => {
      getDynamicTopicCandidates(record.comment).forEach((topic) => {
        if (!commentsByTopic.has(topic.key)) {
          commentsByTopic.set(topic.key, {
            ...topic,
            totalBoundaryScore: 0,
            matchingComments: [],
          });
        }

        const storedTopic = commentsByTopic.get(topic.key);
        storedTopic.totalBoundaryScore += topic.boundaryScore;
        storedTopic.matchingComments.push(record);
      });
    });

    const rankedTopics = Array.from(commentsByTopic.values())
      .map((topic) => {
        const mentionCount = topic.matchingComments.length;
        const phraseCompleteness =
          topic.totalBoundaryScore / (mentionCount * 2);

        return {
          ...topic,
          key: `dynamic-${topic.key.replace(/\s+/g, "-")}`,
          score:
            mentionCount *
              (1 + Math.min(topic.contentWordCount - 1, 3) * 0.2) +
            phraseCompleteness * 0.7 +
            Math.min(topic.words.length - 1, 4) * 0.12 +
            (topic.insightWeight ?? 0) * 0.65 -
            (topic.contentWordCount === 1 ? 0.75 : 0),
        };
      })
      .sort(
        (left, right) =>
          right.score - left.score ||
          right.matchingComments.length - left.matchingComments.length ||
          right.contentWordCount - left.contentWordCount ||
          left.label.localeCompare(right.label),
      );
    const addDistinctTopics = (selectedTopics, candidates, maximumTopicCount) => {
      for (const candidate of candidates) {
        if (selectedTopics.length >= maximumTopicCount) {
          break;
        }

        if (
          !selectedTopics.some((topic) => topicsOverlap(candidate, topic))
        ) {
          selectedTopics.push(candidate);
        }
      }
    };
    const minimumRecurringMentions = Math.min(
      4,
      Math.max(2, Math.ceil(commentRecords.length * 0.03)),
    );

    const rankedTopicsWithSentiment = rankedTopics.map((topic) => {
      const bubbleSentiment = SENTIMENTS.map((sentiment) => ({
        ...sentiment,
        count: topic.matchingComments.filter(
          (commentRecord) => commentRecord.sentiment === sentiment.key,
        ).length,
      })).reduce((current, sentiment) =>
        sentiment.count > current.count ? sentiment : current,
      );
      const sentimentMatchingComments = topic.matchingComments.filter(
        ({ sentiment }) => sentiment === bubbleSentiment.key,
      );

      return {
        ...topic,
        bubbleSentiment,
        matchingComments: sentimentMatchingComments,
      };
    });
    const recurringSpecificTopics = rankedTopicsWithSentiment.filter(
      ({ contentWordCount, matchingComments }) =>
        contentWordCount > 1 && matchingComments.length >= 2,
    );
    const recurringSingleWordTopics = rankedTopicsWithSentiment.filter(
      ({ contentWordCount, matchingComments }) =>
        contentWordCount === 1 &&
        matchingComments.length >= minimumRecurringMentions,
    );
    const fallbackSpecificTopics = rankedTopicsWithSentiment.filter(
      ({ contentWordCount, matchingComments }) =>
        contentWordCount > 1 && matchingComments.length < 2,
    );
    const fallbackSingleWordTopics = rankedTopicsWithSentiment.filter(
      ({ contentWordCount, matchingComments }) =>
        contentWordCount === 1 &&
        matchingComments.length < minimumRecurringMentions,
    );
    const prioritizedTopics = [
      ...recurringSpecificTopics,
      ...fallbackSpecificTopics,
      ...recurringSingleWordTopics,
      ...fallbackSingleWordTopics,
    ];
    const preferredTopics = [];

    addDistinctTopics(
      preferredTopics,
      recurringSpecificTopics,
      MAX_TOPIC_BUBBLES,
    );

    if (preferredTopics.length < minimumTopicBubbleCount) {
      addDistinctTopics(
        preferredTopics,
        fallbackSpecificTopics,
        minimumTopicBubbleCount,
      );
    }

    if (preferredTopics.length < minimumTopicBubbleCount) {
      addDistinctTopics(
        preferredTopics,
        recurringSingleWordTopics,
        minimumTopicBubbleCount,
      );

      if (preferredTopics.length < minimumTopicBubbleCount) {
        addDistinctTopics(
          preferredTopics,
          fallbackSingleWordTopics,
          minimumTopicBubbleCount,
        );
      }
    }

    const targetTopicCount = preferredTopics.length;
    const commentCountsBySentiment = commentRecords.reduce(
      (counts, { sentiment }) => ({
        ...counts,
        [sentiment]: counts[sentiment] + 1,
      }),
      Object.fromEntries(SENTIMENTS.map(({ key }) => [key, 0])),
    );
    const sentimentBubbleRanges = SENTIMENTS.map(({ key }) => {
      const commentShare =
        commentRecords.length === 0
          ? 0
          : commentCountsBySentiment[key] / commentRecords.length;

      return {
        key,
        minimum: Math.ceil(
          Math.max(0, commentShare - TOPIC_SENTIMENT_TOLERANCE) *
            targetTopicCount,
        ),
        maximum: Math.floor(
          Math.min(1, commentShare + TOPIC_SENTIMENT_TOLERANCE) *
            targetTopicCount,
        ),
      };
    });
    const topics = [];

    // Establish only the lower edge of the acceptable range first. The
    // remaining places can then go to the strongest topics across sentiments.
    sentimentBubbleRanges.forEach(({ key, minimum }) => {
      const sentimentTopics = prioritizedTopics.filter(
        ({ bubbleSentiment }) => bubbleSentiment.key === key,
      );
      const selectedSentimentCount = topics.filter(
        ({ bubbleSentiment }) => bubbleSentiment.key === key,
      ).length;

      addDistinctTopics(
        topics,
        sentimentTopics,
        topics.length + Math.max(0, minimum - selectedSentimentCount),
      );
    });

    prioritizedTopics.forEach((candidate) => {
      if (topics.length >= targetTopicCount) {
        return;
      }

      const sentimentRange = sentimentBubbleRanges.find(
        ({ key }) => key === candidate.bubbleSentiment.key,
      );
      const selectedSentimentCount = topics.filter(
        ({ bubbleSentiment }) =>
          bubbleSentiment.key === candidate.bubbleSentiment.key,
      ).length;

      if (selectedSentimentCount < sentimentRange.maximum) {
        addDistinctTopics(topics, [candidate], targetTopicCount);
      }
    });

    // If the available distinct topics cannot fill every sentiment range, use
    // the strongest remaining candidates instead of leaving the chart sparse.
    addDistinctTopics(topics, prioritizedTopics, targetTopicCount);

    const topicsWithSentiment = topics
      .map((topic) => {
        const mentionCount = topic.matchingComments.length;
        const summaryLabel = getDynamicTopicSummaryLabel(topic);

        return {
          ...topic,
          label: summaryLabel,
          tooltip: `${mentionCount} ${topic.bubbleSentiment.label.toLowerCase()} ${
            mentionCount === 1 ? "comment mentions" : "comments mention"
          } “${summaryLabel}”.`,
        };
      })
      .sort(
        (left, right) =>
          right.matchingComments.length - left.matchingComments.length ||
          right.score - left.score ||
          left.label.localeCompare(right.label),
      );

    const topicCounts = topicsWithSentiment.map(
      ({ matchingComments }) => matchingComments.length,
    );
    const smallestTopicCount =
      topicCounts.length === 0 ? 0 : Math.min(...topicCounts);
    const largestTopicCount = Math.max(...topicCounts, 1);
    const topicCountRange = largestTopicCount - smallestTopicCount;
    const minimumBubbleSize = 52;
    const maximumBubbleSize = topicsWithSentiment.length > 18 ? 140 : 158;

    return topicsWithSentiment.map((topic) => {
      const analyzedCommentCount = topic.matchingComments.length;
      const sizeRatio =
        topicCountRange === 0
          ? 0.5
          : (analyzedCommentCount - smallestTopicCount) / topicCountRange;

      return {
        ...topic,
        size: Math.round(
          minimumBubbleSize +
            (maximumBubbleSize - minimumBubbleSize) * sizeRatio,
        ),
        analyzedCommentCount,
      };
    });
  }, [commentSentiments]);

  const packedTopicBubbles = useMemo(
    () => packTopicBubbles(topicSummaries),
    [topicSummaries],
  );

  useEffect(() => {
    if (loading || commentSentiments.length === 0) {
      return undefined;
    }

    let secondAnimationFrame;
    const firstAnimationFrame = window.requestAnimationFrame(() => {
      secondAnimationFrame = window.requestAnimationFrame(() => {
        const charts = [
          doughnutChartRef.current,
          ...horizontalChartRefs.current.values(),
        ];

        charts.forEach((chart) => {
          if (chart) {
            chart.stop();
            chart.reset();
            chart.update();
          }
        });
      });
    });

    return () => {
      window.cancelAnimationFrame(firstAnimationFrame);

      if (secondAnimationFrame) {
        window.cancelAnimationFrame(secondAnimationFrame);
      }
    };
  }, [commentSentiments, loading]);

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (loading) {
    return (
      <Box
        role="status"
        aria-busy="true"
        aria-label="Analyzing comments"
        sx={{
          width: "100%",
          height: "30vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (commentSentiments.length === 0) {
    return <Alert severity="info">There are no comments to analyze.</Alert>;
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "50vh",
        m: 0,
        boxSizing: "border-box",
        p: 1.5,
        bgcolor: "#F5F5F5",
        borderRadius: 2,
      }}
    >
      <Box
        component="section"
        aria-label="Comment sentiment summary"
        sx={{
          ...DASHBOARD_CARD_STYLES,
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          p: { xs: 1.25, sm: 1.5 },
        }}
      >
        {summaryMetrics.map(
          (
            { key, label, value, color, backgroundColor, icon: MetricIcon },
            index,
          ) => (
            <Box
              key={key}
              sx={{
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 1.5, sm: 2 },
                px: { xs: 1, sm: 2 },
                py: 1,
                borderRight: {
                  xs: "none",
                  sm: index % 2 === 0 ? "1px solid #dde2ea" : "none",
                  lg: index < summaryMetrics.length - 1
                    ? "1px solid #dde2ea"
                    : "none",
                },
                borderBottom: {
                  xs: index < summaryMetrics.length - 1
                    ? "1px solid #eef0f4"
                    : "none",
                  sm: index < 2 ? "1px solid #eef0f4" : "none",
                  lg: "none",
                },
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  flex: "0 0 auto",
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 2,
                  color,
                  bgcolor: backgroundColor ?? colorWithAlpha(color, 0.12),
                }}
              >
                <MetricIcon sx={{ fontSize: 34 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    color,
                    fontSize: { xs: "1.6rem", sm: "1.85rem" },
                    fontWeight: 700,
                    lineHeight: 1.05,
                  }}
                >
                  {value}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.65,
                    color: "#24304a",
                    fontSize: "0.92rem",
                    lineHeight: 1.2,
                  }}
                >
                  {label}
                </Typography>
              </Box>
            </Box>
          ),
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "repeat(2, minmax(0, 1fr))",
          },
          gap: 1.5,
          mt: 1.5,
        }}
      >
        <Box
          component="section"
          aria-labelledby="overall-sentiment-heading"
          sx={{
            ...DASHBOARD_CARD_STYLES,
            display: "flex",
            flexDirection: "column",
            p: { xs: 2, sm: 2.5 },
          }}
        >
          <Typography
            component="h2"
            id="overall-sentiment-heading"
            sx={{ color: "#101828", fontSize: "1rem", fontWeight: 700 }}
          >
            Overall Sentiment
          </Typography>

          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: 700,
              height: { xs: 260, sm: 325 },
              mx: "auto",
              mt: 0.5,
            }}
          >
            <Doughnut
              ref={doughnutChartRef}
              data={chartData}
              options={doughnutOptions}
              role="img"
              aria-label="Half doughnut chart showing negative, neutral, and positive comment totals. Click a segment to view its comments."
            />
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                top: { xs: "72%", sm: "74%" },
                width: "60%",
                textAlign: "center",
                transform: "translate(-50%, -50%)",
                pointerEvents: "none",
              }}
            >
              <Typography
                sx={{
                  color: sentimentSummary.dominantSentiment.color,
                  fontSize: { xs: "2.25rem", sm: "2.85rem" },
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {formatPercentage(
                  sentimentSummary.percentages[
                    sentimentSummary.dominantSentiment.key
                  ],
                )}
              </Typography>
              <Typography
                sx={{
                  mt: 0.75,
                  color: sentimentSummary.dominantSentiment.color,
                  fontSize: { xs: "1rem", sm: "1.15rem" },
                  fontWeight: 700,
                  lineHeight: 1.1,
                }}
              >
                {sentimentSummary.dominantSentiment.label}
              </Typography>
              <Typography
                sx={{ mt: 0.75, color: "#344054", fontSize: "0.9rem" }}
              >
                Overall Sentiment
              </Typography>
            </Box>
          </Box>

          <Box
            aria-label="Sentiment legend"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: { xs: 2, sm: 4 },
              mt: "auto",
            }}
          >
            {SENTIMENTS.map(({ key, label, color }) => (
              <Box
                key={key}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Box
                  component="span"
                  sx={{ width: 16, height: 16, bgcolor: color }}
                />
                <Typography sx={{ color: "#344054", fontSize: "0.9rem" }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          component="section"
          aria-labelledby="comment-topics-heading"
          sx={{
            ...DASHBOARD_CARD_STYLES,
            display: "flex",
            flexDirection: "column",
            p: { xs: 2, sm: 2.5 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography
              component="h2"
              id="comment-topics-heading"
              sx={{ color: "#101828", fontSize: "1rem", fontWeight: 700 }}
            >
              What Students Are Talking About
            </Typography>
            <MuiTooltip title="Topic titles are short summaries dynamically extracted from their matching comments. Bubble size reflects matching comments, and each bubble contains only comments with the sentiment shown by its color.">
              <InfoOutlinedIcon
                aria-label="About topic bubbles"
                sx={{ color: "#98a2b3", fontSize: 18 }}
              />
            </MuiTooltip>
          </Box>

          <Box
            sx={{
              width: "100%",
              height: { xs: 320, sm: 370 },
              py: 0.5,
            }}
          >
            {packedTopicBubbles.bubbles.length === 0 ? (
              <Box
                role="status"
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <Typography sx={{ color: "#667085", fontSize: "0.9rem" }}>
                  No distinct topics could be extracted from these comments.
                </Typography>
              </Box>
            ) : (
              <Box
                component="svg"
                viewBox={packedTopicBubbles.viewBox}
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label="Packed bubble chart of topics dynamically extracted from this report. Larger bubbles represent more mentions."
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  "& g[role='button']": {
                    transition: "transform 180ms ease-out",
                  },
                  "& g[role='button']:hover, & g[role='button']:focus": {
                    transform: "scale(1.16)",
                  },
                  "& g[role='button']:hover .bubble-fill": {
                    filter:
                      "brightness(0.94) drop-shadow(0 4px 5px rgba(15, 23, 42, 0.3))",
                  },
                  "& g[role='button']:focus": {
                    outline: "none",
                  },
                  "& g[role='button']:focus circle": {
                    stroke: "#1d2939",
                    strokeWidth: 3,
                  },
                  "@media (prefers-reduced-motion: reduce)": {
                    "& g[role='button']": {
                      transition: "none",
                    },
                  },
                }}
              >
              {packedTopicBubbles.bubbles.map(
                ({
                  key,
                  label,
                  radius,
                  x,
                  y,
                  analyzedCommentCount,
                  bubbleSentiment,
                  matchingComments,
                  tooltip,
                }) => {
                  const labelLines = getBubbleLabelLines(label);
                  const longestLineLength = Math.max(
                    ...labelLines.map((line) => line.length),
                    1,
                  );
                  const requestedFontSize = Math.min(
                    14,
                    Math.max(8, radius * 0.27),
                  );
                  const fontSize = Math.max(
                    6.5,
                    Math.min(
                      requestedFontSize,
                      (radius * 1.75) / (longestLineLength * 0.58),
                    ),
                  );
                  const visibleLineCount = labelLines.length;
                  const firstLineY =
                    y - ((visibleLineCount - 1) * fontSize * 1.05) / 2;
                  const openTopicComments = (event) => {
                    selectedTopicBubbleRef.current = event.currentTarget;
                    setSelectedCommentGroup(
                      createTopicCommentSelection({
                        label,
                        matchingComments,
                      }),
                    );
                  };

                  return (
                    <g
                      key={key}
                      role="button"
                      tabIndex="0"
                      aria-label={`${label}: ${analyzedCommentCount} ${bubbleSentiment.label.toLowerCase()} mentions`}
                      onClick={openTopicComments}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openTopicComments(event);
                        }
                      }}
                      style={{
                        cursor: "pointer",
                        transformBox: "view-box",
                        transformOrigin: `${x}px ${y}px`,
                      }}
                    >
                      <title>{tooltip}</title>
                      <circle
                        className="bubble-fill"
                        cx={x}
                        cy={y}
                        r={radius}
                        fill={bubbleSentiment.color}
                        stroke="#ffffff"
                        strokeWidth={Math.max(1, radius * 0.025)}
                      />
                      <text
                        x={x}
                        y={firstLineY}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="#ffffff"
                        fontFamily="Roboto, sans-serif"
                        fontSize={fontSize}
                        fontWeight="700"
                        pointerEvents="none"
                      >
                        {labelLines.map((line, lineIndex) => (
                          <tspan
                            key={`${key}-line-${lineIndex}`}
                            x={x}
                            dy={lineIndex === 0 ? 0 : fontSize * 1.05}
                          >
                            {line}
                          </tspan>
                        ))}
                      </text>
                    </g>
                  );
                },
              )}
              </Box>
            )}
          </Box>

          <Box
            aria-label="Topic sentiment legend"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: { xs: 2, sm: 4 },
              mt: "auto",
            }}
          >
            {SENTIMENTS.map(({ key, label, color }) => (
              <Box
                key={key}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Box
                  component="span"
                  sx={{ width: 16, height: 16, bgcolor: color }}
                />
                <Typography sx={{ color: "#344054", fontSize: "0.9rem" }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "repeat(2, minmax(0, 1fr))",
          },
          gap: 1.5,
          mt: 1.5,
        }}
      >
        {questionCharts.map(
          ({
            questionId,
            questionText,
            responseCount,
            totalResponses,
            responseRate,
            data,
            options,
          }) => (
            <Box
              component="section"
              key={questionId}
              sx={{
                ...DASHBOARD_CARD_STYLES,
                display: "flex",
                flexDirection: "column",
                p: { xs: 2, sm: 2.5 },
              }}
            >
              <Box
                component="header"
                sx={{
                  display: "grid",
                  gridTemplateColumns: "max-content minmax(0, 1fr)",
                  columnGap: 1,
                  rowGap: 0.4,
                  mb: 0.5,
                  color: "#25304a",
                }}
              >
                <Box
                  component="span"
                  sx={{ fontSize: "0.88rem", fontWeight: 700 }}
                >
                  Question:
                </Box>
                <Box
                  component="h3"
                  sx={{ fontSize: "0.88rem", fontWeight: 400, m: 0 }}
                >
                  {questionText}
                </Box>
                <Box
                  component="span"
                  sx={{ fontSize: "0.82rem", fontWeight: 700 }}
                >
                  Response Rate:
                </Box>
                <Box component="p" sx={{ fontSize: "0.82rem", m: 0 }}>
                  {responseRate.toFixed(1)}% ({responseCount} of {totalResponses})
                </Box>
              </Box>

              <Box sx={{ height: { xs: 220, sm: 250 }, mt: "auto" }}>
                <Bar
                  ref={(chart) => {
                    if (chart) {
                      horizontalChartRefs.current.set(questionId, chart);
                    } else {
                      horizontalChartRefs.current.delete(questionId);
                    }
                  }}
                  data={data}
                  options={options}
                  role="img"
                  aria-label={`Horizontal bar chart showing sentiment percentages for ${questionText}. Click a bar to view its comments.`}
                />
              </Box>
            </Box>
          ),
        )}
      </Box>
      <SentimentCommentsDialog
        selection={selectedCommentGroup}
        onClose={closeCommentsDialog}
      />
    </Box>
  );
}

export { CommentsAnalysis };
